import { onUnmounted, watch } from 'vue'

import { initializeNovu, setNotificationsUnavailable } from '@/lib/services'
import { useNotificationStore } from '@/stores/notifications.stores'
import { useUserStore } from '@/stores/user'

export function useNovuWebsocketService() {
  const userStore = useUserStore()
  const notificationsStore = useNotificationStore()
  let cleanupFunctions: (() => void)[] = []

  const handleNotificationReceived = async () => {
    await Promise.all([notificationsStore.fetchUnread(true), notificationsStore.fetchAll(true)])
  }

  const changeUnreadCount = ({
    result,
  }: {
    result: { total: number; severity: Record<string, number> }
  }) => {
    notificationsStore.changeUnreadCount(result.total)
  }

  const initialize = async (subscriberId: string, hash: string) => {
    setNotificationsUnavailable(false)

    try {
      const novu = initializeNovu(subscriberId, hash)
      const result = await novu.notifications.count({
        read: false,
        archived: false,
      })

      if (result?.error) {
        setNotificationsUnavailable(true)
        return
      }

      notificationsStore.changeUnreadCount(result.data?.count || 0)

      cleanupFunctions.push(
        novu.on('notifications.notification_received', () => void handleNotificationReceived()),
      )
      cleanupFunctions.push(novu.on('notifications.unread_count_changed', changeUnreadCount))
    } catch {
      setNotificationsUnavailable(true)
    }
  }

  const uninitialize = () => {
    if (cleanupFunctions.length) {
      cleanupFunctions.forEach((cleanup) => cleanup())
      cleanupFunctions = []
    }
  }

  watch(
    () => userStore.user,
    async (user) => {
      if (user?.subscriber) {
        await initialize(user.subscriber.id, user.subscriber.hash)
      }
    },
    { immediate: true },
  )

  onUnmounted(() => uninitialize())
}
